import React, { useMemo } from "react";
import { formatValue } from "../widgetFormat";

/**
 * Compact SVG line chart. Zero deps, works with any array of `{date, value}`.
 */
export default function ChartWidget({ definition, payload, onDrilldown }) {
  const data = payload?.data || {};
  const series = Array.isArray(data.series) ? data.series : [];
  const yFormat = data.y_format || "int";
  const total = data.total;
  const clickable = !!payload?.drilldown;

  const { path, points, maxY } = useMemo(() => {
    if (!series.length) return { path: "", points: [], maxY: 0 };
    const maxY = Math.max(1, ...series.map((p) => Number(p.value) || 0));
    const w = 100;
    const h = 40;
    const step = series.length > 1 ? w / (series.length - 1) : 0;
    const pts = series.map((p, i) => {
      const y = h - (Number(p.value || 0) / maxY) * h;
      return { x: i * step, y, ...p };
    });
    const d = pts
      .map((pt, i) => `${i === 0 ? "M" : "L"} ${pt.x.toFixed(2)} ${pt.y.toFixed(2)}`)
      .join(" ");
    return { path: d, points: pts, maxY };
  }, [series]);

  return (
    <div
      data-testid={`widget-chart-${definition.id}`}
      className="bg-white border border-[var(--border)] p-5 min-h-[240px] flex flex-col"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-mono-tech text-[0.6rem] font-bold tracking-[0.25em] text-[var(--text-secondary)]">
            TENDENCIA
          </div>
          <div className="font-display font-bold text-base tracking-tight mt-0.5">
            {definition.title}
          </div>
        </div>
        {total !== undefined && (
          <div className="text-right">
            <div className="font-mono-tech text-[0.55rem] tracking-wider text-[var(--text-secondary)]">
              TOTAL
            </div>
            <div className="font-mono-tech font-black text-lg tabular-nums">
              {formatValue(total, yFormat)}
            </div>
          </div>
        )}
      </div>
      <div className="flex-1 relative">
        {series.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-[var(--text-secondary)]">
            Sin datos en este período
          </div>
        ) : (
          <svg
            viewBox="0 0 100 40"
            preserveAspectRatio="none"
            className="w-full h-full"
            aria-label={definition.title}
          >
            <path d={path} fill="none" stroke="#0E5FFF" strokeWidth="0.8" />
            <path
              d={`${path} L 100 40 L 0 40 Z`}
              fill="#0E5FFF"
              fillOpacity="0.08"
              stroke="none"
            />
            {points.map((p, i) => (
              <circle
                key={i}
                cx={p.x}
                cy={p.y}
                r="0.5"
                fill="#0E5FFF"
              />
            ))}
          </svg>
        )}
      </div>
      <div className="flex items-center justify-between mt-3 font-mono-tech text-[0.55rem] tracking-wider text-[var(--text-secondary)]">
        <span>{series[0]?.date || ""}</span>
        <span>{series[series.length - 1]?.date || ""}</span>
      </div>
      {clickable && (
        <button
          type="button"
          onClick={() => onDrilldown?.(definition, payload)}
          className="mt-3 font-mono-tech text-[0.65rem] font-bold tracking-wider text-[#0E5FFF] hover:underline text-left"
        >
          {payload.drilldown.label || "Ver detalle →"}
        </button>
      )}
    </div>
  );
}
